import { cleanTranscript } from './transcript.js';
import type { VoiceSessionGrant } from '@secretario/shared';

/**
 * Conexão com o provedor da conversa ao vivo, usando a chave temporária que a
 * API emitiu. Tudo o que importa (instruções, voz, ferramentas) já vem travado
 * na chave; aqui só passam áudio, transcrições e chamadas de ferramenta.
 */

export type LiveFunctionCall = { id: string; name: string; args: Record<string, unknown> };

export type LiveHandlers = {
  onReady(): void;
  onAudio(base64: string): void;
  onInputText(text: string): void;
  onOutputText(text: string): void;
  onTurnComplete(): void;
  onInterrupted(): void;
  onToolCall(calls: LiveFunctionCall[]): void;
  /** A pessoa falou por cima: o modelo desistiu destas chamadas. */
  onToolCancelled?(ids: string[]): void;
  /** O modelo que pensa com calma ainda está trabalhando entre um turno e outro. */
  onWorking(working: boolean): void;
  onResumable(handle: string): void;
  /** O provedor vai encerrar a conexão em breve (troca periódica). */
  onGoAway(): void;
  onUsage(usage: Record<string, unknown>): void;
  onClosed(code: number, reason: string): void;
};

type ServerMessage = {
  setupComplete?: unknown;
  usageMetadata?: Record<string, unknown>;
  sessionResumptionUpdate?: { newHandle?: string; resumable?: boolean };
  goAway?: unknown;
  toolCall?: { functionCalls?: LiveFunctionCall[] };
  /** A pessoa interrompeu: estas chamadas não devem mais ser respondidas. */
  toolCallCancellation?: { ids?: string[] };
  serverContent?: {
    modelTurn?: { parts?: { inlineData?: { data?: string } }[] };
    inputTranscription?: { text?: string };
    outputTranscription?: { text?: string };
    interrupted?: boolean;
    turnComplete?: boolean;
    interactionStatus?: unknown;
  };
};

export class LiveConnection {
  private socket: WebSocket | null = null;
  private closedByUs = false;

  constructor(
    private readonly grant: VoiceSessionGrant,
    private readonly handlers: LiveHandlers,
  ) {}

  /** Abre a conexão; com `handle`, retoma a conversa de onde parou. */
  connect(handle?: string): Promise<void> {
    return new Promise((resolve, reject) => {
      const socket = new WebSocket(
        `${this.grant.url}?access_token=${encodeURIComponent(this.grant.token)}`,
      );
      this.socket = socket;
      let ready = false;
      const timer = setTimeout(() => {
        if (!ready) {
          socket.close();
          reject(new Error('live_setup_timeout'));
        }
      }, 15000);
      socket.onopen = () => {
        socket.send(
          JSON.stringify({
            setup: {
              model: `models/${this.grant.model}`,
              ...(handle ? { sessionResumption: { handle } } : {}),
            },
          }),
        );
      };
      socket.onerror = () => {
        // O fechamento logo em seguida traz o motivo.
      };
      socket.onclose = (event) => {
        clearTimeout(timer);
        if (!ready) reject(new Error(`live_closed_${event.code}`));
        if (!this.closedByUs) this.handlers.onClosed(event.code, event.reason);
      };
      socket.onmessage = async (event) => {
        const raw = typeof event.data === 'string' ? event.data : await (event.data as Blob).text();
        let message: ServerMessage;
        try {
          message = JSON.parse(raw) as ServerMessage;
        } catch {
          return;
        }
        if (message.usageMetadata) this.handlers.onUsage(message.usageMetadata);
        if (message.setupComplete !== undefined) {
          ready = true;
          clearTimeout(timer);
          this.handlers.onReady();
          resolve();
          return;
        }
        const handleUpdate = message.sessionResumptionUpdate;
        if (handleUpdate?.newHandle && handleUpdate.resumable !== false) {
          this.handlers.onResumable(handleUpdate.newHandle);
        }
        if (message.goAway !== undefined) this.handlers.onGoAway();
        if (message.toolCall?.functionCalls?.length) {
          this.handlers.onToolCall(message.toolCall.functionCalls);
        }
        if (message.toolCallCancellation?.ids?.length) {
          this.handlers.onToolCancelled?.(message.toolCallCancellation.ids);
        }
        const content = message.serverContent;
        if (!content) return;
        if (content.interrupted) this.handlers.onInterrupted();
        for (const part of content.modelTurn?.parts ?? []) {
          if (part.inlineData?.data) this.handlers.onAudio(part.inlineData.data);
        }
        const heard = cleanTranscript(content.inputTranscription?.text ?? '');
        if (heard) this.handlers.onInputText(heard);
        const said = cleanTranscript(content.outputTranscription?.text ?? '');
        if (said) this.handlers.onOutputText(said);
        if (content.interactionStatus !== undefined) {
          this.handlers.onWorking(
            JSON.stringify(content.interactionStatus).includes('IN_PROGRESS'),
          );
        }
        if (content.turnComplete) this.handlers.onTurnComplete();
      };
    });
  }

  sendAudio(base64: string): void {
    this.send({ realtimeInput: { audio: { data: base64, mimeType: 'audio/pcm;rate=16000' } } });
  }

  /** Texto como se a pessoa tivesse falado (pergunta levada ao modo que pensa com calma, avisos). */
  sendText(text: string): void {
    this.send({
      clientContent: { turns: [{ role: 'user', parts: [{ text }] }], turnComplete: true },
    });
  }

  sendToolResponse(id: string, name: string, response: Record<string, unknown>): void {
    this.send({ toolResponse: { functionResponses: [{ id, name, response }] } });
  }

  open(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  close(): void {
    this.closedByUs = true;
    try {
      this.socket?.close();
    } catch {
      // já fechada
    }
    this.socket = null;
  }

  private send(message: unknown): void {
    if (this.socket?.readyState === WebSocket.OPEN) this.socket.send(JSON.stringify(message));
  }
}
